import React, { useState, useEffect } from "react";
import Div100vh from "react-div-100vh";
import GoBackButton from "../buttons/GoBackButton";
import TenantLandordCharges from "@/assets/images/tenant-ll-terms-bg.webp";
import { trackPageView } from "@/lib/helper";
import { ArrowRight } from "lucide-react";
import { NCR_REGIONS } from "@/lib/data"; // Assuming NCR_REGIONS includes "delhi", "NCR", "gurgaon"
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import { useRecoilValue } from "recoil";

export default function CirclePeOffers({ setBenefitsDrawer }) {
  const [enteredRent, setEnteredRent] = useState(""); // Entered Rent
  const [leaseTenure, setLeaseTenure] = useState(10); // Lease Tenure
  const [tenantRent, setTenantRent] = useState(0); // Tenant's Monthly Rent
  const [landlordWillGet, setLandlordWillGet] = useState(0); // Landlord's Advance Rent
  const [showResult, setShowResult] = useState(false);
  const [cityType, setCityType] = useState("others"); // Determine if city is NCR or others
  const userData = useRecoilValue(userDataAtom);

  // Dummy user city (Replace with actual data from user state or API)
  const userCity = userData.moveInCity;

  useEffect(() => {
    // Determine city type based on userCity
    if (NCR_REGIONS.includes(userCity)) {
      setCityType("ncr");
      setLeaseTenure(8); // Update lease tenure for NCR
    } else {
      setCityType("others");
      setLeaseTenure(10); // Update lease tenure for other cities
    }
  }, [userCity]);

  const handleCheckNow = () => {
    calculateRentDetails();
    setShowResult(true);
  };

  const calculateRentDetails = () => {
    const rent = parseFloat(enteredRent || 0);

    // Rates based on city type
    const tenantRate = cityType === "ncr" ? 0.03 : 0.04; // 3% for NCR, 4% for others
    const landlordRate = cityType === "ncr" ? 0.025 : 0.02; // 2.5% for NCR, 2% for others

    // Calculations (excluding GST)
    const calculatedTenantRent = rent + rent * tenantRate; // Tenant's Rent (excluding GST)
    const calculatedLandlordRent =
      rent * leaseTenure - rent * leaseTenure * landlordRate; // Landlord's Rent (excluding GST)

    // Update state with calculated values
    setTenantRent(formatNumberWithComma(calculatedTenantRent.toFixed(2)));
    setLandlordWillGet(
      formatNumberWithComma(calculatedLandlordRent.toFixed(2))
    );
  };

  function formatNumberWithComma(value) {
    if (isNaN(value)) return "Invalid input"; // Handle non-numeric inputs

    const roundedValue = Math.round(value);

    const numberString = roundedValue.toString();
    const lastThreeDigits = numberString.slice(-3);
    const remainingDigits = numberString.slice(0, -3);

    return (
      remainingDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
      (remainingDigits ? "," : " ") +
      lastThreeDigits
    );
  }

  useEffect(() => {
    trackPageView("Charges Screen", "Home Page CTA");
  }, []);

  return (
    <Div100vh className="flex flex-col gap-4 bg-[#010101] overflow-y-scroll overflow-x-hidden">
      <div className="flex flex-col gap-4 bg-no-repeat bg-cover bg-center h-auto">
        <div className="pt-6 px-6">
          <GoBackButton
            customBackSubmit={() =>
              setBenefitsDrawer((prevState) => !prevState)
            }
          />
        </div>
        <div
          className="py-4 bg-no-repeat bg-center bg-contain mx-2"
          style={{
            backgroundImage: `url(${TenantLandordCharges})`,
          }}
        >
          <ForTenantsAndLandlords cityType={cityType} />
        </div>
      </div>

      <div className="bg-black text-white flex items-center justify-centers">
        <div className="w-full max-w-md space-y-6">
          <h1 className="text-lg font-medium px-8 text-left">
            Your Rent Math, Made Easy!
          </h1>
          <div className="flex flex-col w-full pl-8 pr-4">
            <div className="flex gap-2 items-center">
              <div className="border border-gray-600 p-3 h-full w-full">
                <input
                  type="number"
                  min="0"
                  placeholder="Enter Rent"
                  className="bg-black text-gray-400 placeholder-gray-600 px-4 py-2 flex-1 focus:outline-none"
                  value={enteredRent}
                  onChange={(e) => {
                    setEnteredRent(e.target.value);
                    setShowResult(false);
                  }}
                />
              </div>
              <button
                className="flex flex-col bg-white h-full text-black p-3 text-xs font-medium text-left"
                onClick={handleCheckNow}
              >
                <span>Check</span>
                <span className="flex min-w-max">
                  Now <ArrowRight className="size-5" />
                </span>
              </button>
            </div>
            <span className="flex justify-end w-full text-3xs text-gray-500">
              *Exclusive of taxes
            </span>
          </div>
          <div className="mx-8 flex justify-between items-center border-b border-gray-600 pb-4">
            <span className="text-sm text-[#eee]">Lease Tenure</span>
            <span className="text-[18px] text-[#888]">
              {leaseTenure} months
            </span>
          </div>

          <div className="mx-8 flex justify-between items-center border-b border-gray-600 pb-4">
            <div>
              <span className="block text-sm text-[#eee] mb-2">
                Tenant's monthly rent
              </span>
              <span className="block text-xs text-gray-500">
                {cityType === "ncr" ? "3%" : "4%"}
              </span>
            </div>
            <div className="text-lg font-medium text-blue-400 tracking-wider">
              {showResult && tenantRent !== "0" ? `₹ ${tenantRent}` : "---"}
            </div>
          </div>

          <div className="mx-8 flex justify-between items-center">
            <div>
              <span className="block text-sm text-[#eee] mb-2">
                Landlord will get
              </span>
              <span className="block text-xs text-gray-500">
                {cityType === "ncr" ? "2.5%" : "2%"}
              </span>
            </div>
            <div className="text-lg font-medium text-blue-400 tracking-wider">
              {showResult && landlordWillGet !== "0"
                ? `₹ ${landlordWillGet}`
                : "---"}
            </div>
          </div>
        </div>
      </div>
    </Div100vh>
  );
}

const ForTenantsAndLandlords = ({ cityType }) => {
  return (
    <div className="text-white flex items-center justify-center my-4 px-8">
      <div className="text-left space-y-6">
        {/* For Tenants Section */}
        <div>
          <h2 className="text-[18px] font-semibold uppercase bg-gradient-to-r from-green-100 via-green-400 to-green-600 text-transparent bg-clip-text tracking-wider">
            For Tenants
          </h2>
          <p className="text-[#ccc] mt-2 text-sm">
            <span className="font-semibold text-[#eee] leading-6">
              {cityType === "ncr" ? "3%" : "4%"}
            </span>{" "}
            of platform fees added to your <br />
            monthly rent
          </p>
          {/* <p className="text-[#aaa] text-xs italic mt-1">GST will be added</p> */}
        </div>

        {/* For Landlords Section */}
        <div>
          <h2 className="text-[18px] font-semibold uppercase bg-gradient-to-r from-blue-100 via-blue-400 to-blue-500 text-transparent bg-clip-text tracking-wide">
            For Landlords
          </h2>
          <p className="text-[#ccc] mt-2 text-sm leading-6">
            Advance Rent of entire lease at{" "}
            <span className="font-bold">
              {cityType === "ncr" ? "2.5%" : "2%"}
            </span>{" "}
            discount
          </p>
          <p className="text-white uppercase font-medium tracking-wider italic mt-1 text-xs">
            + 1 month security deposit
          </p>
        </div>
      </div>
    </div>
  );
};
